import {
  BeginUploadRequestSchema,
  BeginUploadResponseSchema,
  CreateChatPollResponseSchema,
  CreateChatRequestSchema,
  CreateWorkstreamRequestSchema,
  CreateWorkstreamResponseSchema,
  DeleteArtifactResponseSchema,
  DeleteMemoryResponseSchema,
  ListMemberAgentsResponseSchema,
  ListOrgAgentsResponseSchema,
  MemberAgentResponseSchema,
  RemoveMemberAgentResponseSchema,
  BuilderResponseSchema,
  CreateBuilderResponseSchema,
  ListDraftsResponseSchema,
  LiveVersionResponseSchema,
  ListLibraryResponseSchema,
  ShareRequestResponseSchema,
  ListReviewsResponseSchema,
  DecideReviewResponseSchema,
  RetireAgentResponseSchema,
  DeleteWorkstreamResponseSchema,
  BeginEvalDocumentUploadResponseSchema,
  EvalDocumentListResponseSchema,
  EvalDocumentTextResponseSchema,
  EvalRunListResponseSchema,
  EvalRunResponseSchema,
  EvalSetListResponseSchema,
  EvalSetResponseSchema,
  PutEvalSetRequestSchema,
  type PutEvalSetRequest,
  StaffMeResponseSchema,
  type StaffMeResponse,
  MeResponseSchema,
  PlanResponseSchema,
  type MeResponse,
  type PlanResponse,
  BudgetPutResponseSchema,
  ChangeRoleRequestSchema,
  InviteRequestSchema,
  InviteResponseSchema,
  InviteWithdrawnResponseSchema,
  MemberChangeResponseSchema,
  MemberRemovedResponseSchema,
  MembersResponseSchema,
  type ChangeRoleRequest,
  type InviteRequest,
  type MembersResponse,
  BudgetExtensionRequestSchema,
  BudgetExtensionResponseSchema,
  type BudgetExtensionData,
  type BudgetExtensionRequest,
  BudgetResponseSchema,
  BudgetSettingsRequestSchema,
  type BudgetResponse,
  type BudgetSettings,
  type BudgetSettingsRequest,
  RenameWorkstreamRequestSchema,
  RenameWorkstreamResponseSchema,
  FinalizeUploadRequestSchema,
  FinalizeUploadResponseSchema,
  GetArtifactResponseSchema,
  ListArtifactsResponseSchema,
  ListMemoryResponseSchema,
  ListMessagesResponseSchema,
  ListWorkstreamsResponseSchema,
  MemoryEntryResponseSchema,
  ProblemDetailsSchema,
  UsageLedgerResponseSchema,
  UsageReportResponseSchema,
  UsageResponseSchema,
  ActivityExportRequestSchema,
  ActivityExportResponseSchema,
  ActivityResponseSchema,
  type ActivityResponse,
  type UsageLedgerResponse,
  WorkstreamChatPollRequestSchema,
  WorkstreamChatPollResponseSchema,
  WorkstreamChatRequestSchema,
  WorkstreamChatSubmitResponseSchema,
  type BeginUploadRequest,
  type BeginUploadResponse,
  type CreateChatRequest,
  type CreateChatResponse,
  type CreateWorkstreamRequest,
  type CreateWorkstreamResponse,
  type DeleteArtifactResponse,
  type DeleteMemoryResponse,
  type DeleteWorkstreamResponse,
  type RenameWorkstreamRequest,
  type RenameWorkstreamResponse,
  type FinalizeUploadRequest,
  type FinalizeUploadResponse,
  type GetArtifactResponse,
  type ListArtifactsResponse,
  type ListMemoryResponse,
  type ListMessagesResponse,
  type MemoryEntryResponse,
  type ListWorkstreamsResponse,
  type Message,
  type ProblemDetails,
  type TurnEvent,
  type UsageReportResponse,
  type UsageResponse,
  type WorkstreamChatPollRequest,
  type WorkstreamChatPollResponse,
  type WorkstreamChatRequest,
  type WorkstreamChatSubmitResponse,
  type WorkstreamTurnResult,
} from '@agentisai/shared-types';
import { config } from '../config';

const API_PREFIX = '/api/v1';

/** Typed error thrown on any non-2xx response. */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails;

  constructor(status: number, problem: ProblemDetails) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }
}

interface RequestOptions {
  signal?: AbortSignal;
  getAccessToken: () => Promise<string>;
}

/**
 * Read a JSON body defensively. An infra-layer error page (an API Gateway /
 * CloudFront timeout, a WAF block) can be non-JSON (HTML / plain text / empty), so
 * `response.json()` throws a `SyntaxError` — return `undefined` so the caller falls
 * back to a status-derived problem. A genuine cancellation (`AbortError`) or a
 * mid-body network drop (`TypeError`) is NOT a "body isn't JSON" case, so it
 * propagates unchanged — a cancelled request stays a cancellation, never a spurious
 * contract error.
 */
async function readJsonBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch (err) {
    if (err instanceof SyntaxError) return undefined;
    throw err;
  }
}

/**
 * Coerce a non-2xx response into a {@link ProblemDetails}. The BFF's own errors are
 * RFC-9457 problem documents and pass through verbatim; an infra-layer error (a bare
 * `{ message }` gateway body, HTML, or an empty body on a Lambda / API-Gateway
 * timeout) is NOT problem+json, so we synthesise one from the status. This keeps
 * every non-2xx classified as an {@link ApiError} — so `describeError` renders a
 * truthful server / auth / rate-limit message — instead of letting
 * `ProblemDetailsSchema.parse` throw a `ZodError`, which `describeError` would
 * mislabel as the input-validation line "Please enter a message before sending."
 * (the exact confusing symptom of a BFF→agent→Bedrock timeout).
 */
function problemForStatus(status: number, raw: unknown): ProblemDetails {
  const parsed = ProblemDetailsSchema.safeParse(raw);
  if (parsed.success) return parsed.data;
  return {
    type: 'about:blank',
    title: status >= 500 ? 'Server error' : 'Request error',
    status,
    detail: '',
    instance: '',
  };
}

/**
 * Interpret a fetched response uniformly (shared by the POST helpers and the GET
 * `listWorkstreams`): a non-2xx throws an {@link ApiError} — the BFF's problem+json,
 * or a status-derived synthetic when an infra-layer error isn't problem+json; a 2xx
 * is parsed through `parse`, and a body that drifts from the contract becomes a
 * generic error, never the request-validation ZodError path.
 */
async function handleResponse<T>(response: Response, parse: (raw: unknown) => T): Promise<T> {
  const raw: unknown = await readJsonBody(response);

  if (!response.ok) {
    throw new ApiError(response.status, problemForStatus(response.status, raw));
  }

  try {
    return parse(raw);
  } catch (cause) {
    // A 2xx whose body doesn't match the contract is a server / deploy drift, not
    // user input — surface a generic error (describeError's fallback → GENERIC),
    // never the request-validation ZodError path ("enter a message before sending").
    throw new Error('The server sent an unexpected response.', { cause });
  }
}

async function request<TBody, TResponse>(
  path: string,
  body: TBody,
  parseResponse: (raw: unknown) => TResponse,
  { signal, getAccessToken }: RequestOptions,
): Promise<TResponse> {
  const token = await getAccessToken();

  const response = await fetch(`${config.apiBaseUrl}${API_PREFIX}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
    signal,
  });

  return handleResponse(response, parseResponse);
}

/**
 * One pre-create discovery turn (ADR-0074): submit to
 * `POST /api/v1/workstreams/create/chat` (`202` + turnId), then poll
 * `…/create/chat/poll` until done. Resolves with the same `{ data, meta }`
 * shape the route returned when it was synchronous, so callers are unchanged.
 */
export async function sendChatTurn(
  body: CreateChatRequest,
  options: RequestOptions,
  onProgress?: (events: readonly TurnEvent[]) => void,
): Promise<CreateChatResponse> {
  const validated = CreateChatRequestSchema.parse(body);
  const submitted = await request(
    '/workstreams/create/chat',
    validated,
    (raw) => WorkstreamChatSubmitResponseSchema.parse(raw),
    options,
  );
  const { turnId, userMessage } = submitted.data;
  const poll = await pollUntilDone(
    () =>
      request(
        '/workstreams/create/chat/poll',
        WorkstreamChatPollRequestSchema.parse({ workstreamId: body.workstreamId, turnId }),
        (raw) => CreateChatPollResponseSchema.parse(raw),
        options,
      ),
    onProgress,
  );
  return { data: { userMessage, ...poll.data.result }, meta: poll.meta };
}

export function createWorkstream(
  body: CreateWorkstreamRequest,
  options: RequestOptions,
): Promise<CreateWorkstreamResponse> {
  const validated = CreateWorkstreamRequestSchema.parse(body);
  return request(
    '/workstreams/create',
    validated,
    (raw) => CreateWorkstreamResponseSchema.parse(raw),
    options,
  );
}

/**
 * `POST /api/v1/workstreams/chat` — submit a chat turn inside an existing
 * workstream (async, ADR-0037): returns `202` with the `turnId` to poll.
 * `focusId` is the constant `'default'` implicit focus until multi-focus
 * switching lands; `agentName`, when present, is the "invite agent" hint
 * forwarded to the Librarian pipeline.
 */
export function submitWorkstreamChatTurn(
  body: WorkstreamChatRequest,
  options: RequestOptions,
): Promise<WorkstreamChatSubmitResponse> {
  const validated = WorkstreamChatRequestSchema.parse(body);
  return request(
    '/workstreams/chat',
    validated,
    (raw) => WorkstreamChatSubmitResponseSchema.parse(raw),
    options,
  );
}

/** `POST /api/v1/workstreams/chat/poll` — read an async turn's status (ADR-0037). */
export function pollWorkstreamChatTurn(
  body: WorkstreamChatPollRequest,
  options: RequestOptions,
): Promise<WorkstreamChatPollResponse> {
  const validated = WorkstreamChatPollRequestSchema.parse(body);
  return request(
    '/workstreams/chat/poll',
    validated,
    (raw) => WorkstreamChatPollResponseSchema.parse(raw),
    options,
  );
}

/**
 * Poll cadence for an in-flight turn — cheap (one DynamoDB read behind the
 * BFF). Env-tunable solely so the unit suite isn't 2 s slower per turn.
 */
const TURN_POLL_INTERVAL_MS = Number(import.meta.env['VITE_TURN_POLL_INTERVAL_MS'] ?? 2_000);
/**
 * Client-side ceiling on a turn — the backstop for a turn that died without a
 * record. Matched to the agent Lambda's 15-min timeout + async event max-age
 * (workstreamagent-stack.ts) so the UI waits out the full server budget for a
 * long staged/tool turn instead of abandoning one the agent is still running.
 */
const TURN_POLL_TIMEOUT_MS = 15 * 60_000;

/**
 * One full async chat turn (ADR-0037): submit, then poll every
 * {@link TURN_POLL_INTERVAL_MS} until the agent completes (minutes-long turns
 * are the point — the old sync request died at the ~29 s gateway budget).
 * Resolves with the server-stamped user message (from the submit ack) and the
 * completed turn result; throws on a failed turn or on timeout.
 * `onProgress` (ADR-0042) fires with the cumulative checkpoint trail each
 * time a poll carries one, so the UI narrates the turn while it runs.
 */
export async function runWorkstreamChatTurn(
  body: WorkstreamChatRequest,
  options: RequestOptions,
  onProgress?: (events: readonly TurnEvent[]) => void,
): Promise<{ userMessage: Message; result: WorkstreamTurnResult }> {
  const submitted = await submitWorkstreamChatTurn(body, options);
  const { turnId, userMessage } = submitted.data;
  const poll = await pollUntilDone(
    () => pollWorkstreamChatTurn({ workstreamId: body.workstreamId, turnId }, options),
    onProgress,
  );
  return { userMessage, result: poll.data.result };
}

/**
 * Consecutive failed polls tolerated before a turn is given up on — at the 2 s
 * cadence, ~10 s of network or gateway trouble. The server-side turn is unaffected
 * either way; this only decides how long the browser keeps asking.
 */
const MAX_POLL_MISSES = 5;

/** A poll failure worth retrying: no response at all, or a gateway/server hiccup. */
function isTransientPollError(err: unknown): boolean {
  if (err instanceof ApiError) return err.status >= 500 || err.status === 408 || err.status === 429;
  // `fetch` rejects with a TypeError on a network failure (an AbortError is a
  // DOMException, so a cancelled poll is not retried).
  return err instanceof TypeError;
}

/**
 * Poll an async turn (ADR-0037) every {@link TURN_POLL_INTERVAL_MS} until it
 * completes; throws on a failed turn, on timeout, or after
 * {@link MAX_POLL_MISSES} consecutive transient poll failures. Shared by the pre-create
 * (ADR-0074) and post-create turns — only the poll call and `result` differ.
 */
async function pollUntilDone<R>(
  poll: () => Promise<{
    data: { status: 'running' | 'complete' | 'failed'; result?: R; events?: TurnEvent[] };
    meta: CreateChatResponse['meta'];
  }>,
  onProgress?: (events: readonly TurnEvent[]) => void,
): Promise<{ data: { result: R }; meta: CreateChatResponse['meta'] }> {
  const deadline = Date.now() + TURN_POLL_TIMEOUT_MS;
  let misses = 0;
  for (;;) {
    await new Promise((resolve) => setTimeout(resolve, TURN_POLL_INTERVAL_MS));
    let res;
    try {
      res = await poll();
      misses = 0;
    } catch (err) {
      // A dropped poll is not a dropped turn: the agent keeps running and keeps
      // its workstream lock, so giving up here re-enabled Create (and invited a
      // resend) against a turn that was still holding it. Ride out a short blip;
      // a real refusal (4xx, abort, contract error) still fails at once.
      if (!isTransientPollError(err) || ++misses > MAX_POLL_MISSES || Date.now() >= deadline) {
        throw err;
      }
      continue;
    }
    if (res.data.events && res.data.events.length > 0) onProgress?.(res.data.events);
    if (res.data.status === 'complete' && res.data.result) {
      return { data: { result: res.data.result }, meta: res.meta };
    }
    if (res.data.status === 'failed') {
      throw new Error('The agents could not complete this turn. Please try again.');
    }
    if (Date.now() >= deadline) {
      throw new Error('This turn is taking too long — please try again.');
    }
  }
}

/**
 * `GET /api/v1/workstreams` — the caller's workstreams (S-C1).
 *
 * Parsed through `ListWorkstreamsResponseSchema` (A2 / ADR-0033) so a server
 * shape drift surfaces at the boundary instead of nullable-everything at
 * render. Non-2xx throws `ApiError` for the existing error sink to route.
 */
export async function listWorkstreams(options: RequestOptions): Promise<ListWorkstreamsResponse> {
  const token = await options.getAccessToken();

  const response = await fetch(`${config.apiBaseUrl}${API_PREFIX}/workstreams`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    signal: options.signal,
  });

  return handleResponse(response, (raw) => ListWorkstreamsResponseSchema.parse(raw));
}

/**
 * List one workstream's artifacts (Drop 6 / S09) — `GET /workstreams/{id}/artifacts`.
 * Mirrors {@link listWorkstreams}: bearer-auth, forwards the query `signal` so an
 * abandoned navigation / superseded fetch cancels, and validates the tolerant-out
 * envelope. The workstream id is a uuid but is encodeURIComponent'd defensively.
 */
export async function listArtifacts(
  workstreamId: string,
  options: RequestOptions,
): Promise<ListArtifactsResponse> {
  const token = await options.getAccessToken();

  const response = await fetch(
    `${config.apiBaseUrl}${API_PREFIX}/workstreams/${encodeURIComponent(workstreamId)}/artifacts`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      signal: options.signal,
    },
  );

  return handleResponse(response, (raw) => ListArtifactsResponseSchema.parse(raw));
}

/**
 * List one workstream's persisted transcript (Drop 7 / S05, ADR-0047) —
 * `GET /workstreams/{id}/messages`. Mirrors {@link listArtifacts}: bearer-auth,
 * forwards the query `signal`, validates the tolerant-out envelope. Seeds the
 * client-held chat on reopen; an empty list is a valid (never-run/aged-out) state.
 */
export async function listMessages(
  workstreamId: string,
  options: RequestOptions,
): Promise<ListMessagesResponse> {
  const token = await options.getAccessToken();

  const response = await fetch(
    `${config.apiBaseUrl}${API_PREFIX}/workstreams/${encodeURIComponent(workstreamId)}/messages`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      signal: options.signal,
    },
  );

  return handleResponse(response, (raw) => ListMessagesResponseSchema.parse(raw));
}

/**
 * Fetch one artifact for viewing (Drop 6 / S11) —
 * `GET /workstreams/{id}/artifacts/{artifactId}`. Returns the file's metadata plus,
 * for text-family types under a size cap, the full `content` for inline rendering,
 * and a freshly-presigned `url` for download / binary. A 404 (unknown/deleted id)
 * throws {@link ApiError} with `status: 404` — the View pane self-heals on it.
 * Both path segments are `encodeURIComponent`'d defensively.
 */
export async function getArtifact(
  workstreamId: string,
  artifactId: string,
  options: RequestOptions,
): Promise<GetArtifactResponse> {
  const token = await options.getAccessToken();

  const response = await fetch(
    `${config.apiBaseUrl}${API_PREFIX}/workstreams/${encodeURIComponent(
      workstreamId,
    )}/artifacts/${encodeURIComponent(artifactId)}`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      signal: options.signal,
    },
  );

  return handleResponse(response, (raw) => GetArtifactResponseSchema.parse(raw));
}

/**
 * Hard-delete one artifact (Drop 6 / S10) —
 * `DELETE /workstreams/{id}/artifacts/{artifactId}`. The BFF is the sole tenant
 * gate (ownership check) and the delete is idempotent server-side (an unknown id
 * is a no-op success), so a 2xx always means "gone"; a non-2xx is a genuine
 * failure and throws {@link ApiError} for the caller to surface. Both path
 * segments are `encodeURIComponent`'d defensively (the id is an opaque uuid).
 */
export async function deleteArtifact(
  workstreamId: string,
  artifactId: string,
  options: RequestOptions,
): Promise<DeleteArtifactResponse> {
  const token = await options.getAccessToken();

  const response = await fetch(
    `${config.apiBaseUrl}${API_PREFIX}/workstreams/${encodeURIComponent(
      workstreamId,
    )}/artifacts/${encodeURIComponent(artifactId)}`,
    {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      signal: options.signal,
    },
  );

  return handleResponse(response, (raw) => DeleteArtifactResponseSchema.parse(raw));
}

/**
 * Phase 1 (begin) of the Drop 8 upload flow —
 * `POST /workstreams/{id}/artifacts`. Returns the presigned-POST `url` +
 * `fields` the SPA replays verbatim (Phase 2, `uploadFileToS3`) plus the
 * minted `artifactId` finalize (Phase 3) commits under. A `415` (unlisted
 * `contentType`) throws {@link ApiError} — no URL is ever signed for it.
 */
export function beginUpload(
  workstreamId: string,
  body: BeginUploadRequest,
  options: RequestOptions,
): Promise<BeginUploadResponse> {
  const validated = BeginUploadRequestSchema.parse(body);
  return request(
    `/workstreams/${encodeURIComponent(workstreamId)}/artifacts`,
    validated,
    (raw) => BeginUploadResponseSchema.parse(raw),
    options,
  );
}

/**
 * Phase 3 (finalize) of the Drop 8 upload flow —
 * `POST /workstreams/{id}/artifacts/{artifactId}/complete`. Commits the
 * uploaded object (verify + untag + ledger append) once Phase 2's S3 POST has
 * landed. `contentType`/`sizeBytes` are never sent — the agent trusts only its
 * own `HeadObject` (design doc "Finalize trusts S3, not the client"). A `415`
 * (magic-byte/UTF-8 verification failed) throws {@link ApiError}; the caller
 * (`useUploadArtifact`) retries this call once before giving up.
 */
export function finalizeUpload(
  workstreamId: string,
  artifactId: string,
  body: FinalizeUploadRequest,
  options: RequestOptions,
): Promise<FinalizeUploadResponse> {
  const validated = FinalizeUploadRequestSchema.parse(body);
  return request(
    `/workstreams/${encodeURIComponent(workstreamId)}/artifacts/${encodeURIComponent(artifactId)}/complete`,
    validated,
    (raw) => FinalizeUploadResponseSchema.parse(raw),
    options,
  );
}

/**
 * Rename a workstream — `PATCH /workstreams/{id}` with `{ title }`. The BFF is the
 * sole tenant gate (404 when the caller doesn't own it) and returns the STORED,
 * trimmed title, so callers needn't guess what was persisted. The body is parsed
 * against the shared request schema first so a bad title never leaves the browser.
 */
export async function renameWorkstream(
  workstreamId: string,
  body: RenameWorkstreamRequest,
  options: RequestOptions,
): Promise<RenameWorkstreamResponse> {
  const validated = RenameWorkstreamRequestSchema.parse(body);
  const token = await options.getAccessToken();

  const response = await fetch(
    `${config.apiBaseUrl}${API_PREFIX}/workstreams/${encodeURIComponent(workstreamId)}`,
    {
      method: 'PATCH',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(validated),
      signal: options.signal,
    },
  );

  return handleResponse(response, (raw) => RenameWorkstreamResponseSchema.parse(raw));
}

/**
 * Hard-delete a whole workstream (its agent state + chat + every S3 artifact,
 * then the BFF row) — `DELETE /workstreams/{id}`. The BFF is the sole tenant
 * gate (ownership check) and orders the delete agent-first / BFF-row-last, so a
 * 2xx means the workstream is fully gone; a non-2xx is a genuine failure (the
 * workstream is left intact + retryable) and throws {@link ApiError} for the
 * caller to surface. The id is a uuid but is `encodeURIComponent`'d defensively.
 */
export async function deleteWorkstream(
  workstreamId: string,
  options: RequestOptions,
): Promise<DeleteWorkstreamResponse> {
  const token = await options.getAccessToken();

  const response = await fetch(
    `${config.apiBaseUrl}${API_PREFIX}/workstreams/${encodeURIComponent(workstreamId)}`,
    {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      signal: options.signal,
    },
  );

  return handleResponse(response, (raw) => DeleteWorkstreamResponseSchema.parse(raw));
}

/**
 * List one workstream's memory (ADR-0056) — `GET /workstreams/{id}/memory`.
 * Mirrors {@link listArtifacts}: bearer-auth, forwards the query `signal`,
 * validates the tolerant-out envelope. An empty list is a valid state (a
 * workstream that has not learned anything yet).
 */
export async function listMemory(
  workstreamId: string,
  options: RequestOptions,
): Promise<ListMemoryResponse> {
  const token = await options.getAccessToken();

  const response = await fetch(
    `${config.apiBaseUrl}${API_PREFIX}/workstreams/${encodeURIComponent(workstreamId)}/memory`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      signal: options.signal,
    },
  );

  return handleResponse(response, (raw) => ListMemoryResponseSchema.parse(raw));
}

/**
 * `GET /api/v1/workstreams/usage` — this period's consumption for the caller +
 * the org aggregate (D13-05/07; registered as an exact-match route ahead of
 * the `/workstreams/{id}/…` templates — `apps/ui/api/src/index.ts`). Mirrors
 * {@link listWorkstreams}: bearer-auth, forwards the query `signal`, validates
 * the tolerant-out envelope.
 */
export async function getUsage(options: RequestOptions): Promise<UsageResponse> {
  const token = await options.getAccessToken();

  const response = await fetch(`${config.apiBaseUrl}${API_PREFIX}/workstreams/usage`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    signal: options.signal,
  });

  return handleResponse(response, (raw) => UsageResponseSchema.parse(raw));
}

/**
 * `GET /api/v1/workstreams/usage/report` — Agent Monitor's role-shaped report
 * (ADR-0071 §3, D15-04). `period` (`YYYY-MM`) is omitted for the current one,
 * which the server decides.
 */
export function getUsageReport(
  period: string | undefined,
  options: RequestOptions,
): Promise<UsageReportResponse> {
  const query = period === undefined ? '' : `?${new URLSearchParams({ period })}`;
  return getJson(`/workstreams/usage/report${query}`, options, (raw) =>
    UsageReportResponseSchema.parse(raw),
  );
}

/**
 * `GET /api/v1/workstreams/usage/ledger` — one row per billable turn for `period`
 * (ADR-0076 §3, D16-03). Shaped by role server-side: a Member gets only their own.
 */
export function getUsageLedger(
  period: string | undefined,
  options: RequestOptions,
): Promise<UsageLedgerResponse> {
  const query = period === undefined ? '' : `?${new URLSearchParams({ period })}`;
  return getJson(`/workstreams/usage/ledger${query}`, options, (raw) =>
    UsageLedgerResponseSchema.parse(raw),
  );
}

/** The org's latest activity, newest first — `GET /workstreams/activity` (Owner/Admin; ADR-0076 §2). */
export function listActivity(options: RequestOptions): Promise<ActivityResponse> {
  return getJson('/workstreams/activity', options, (raw) => ActivityResponseSchema.parse(raw));
}

/**
 * Report a CSV export the browser produced — `POST /workstreams/activity/export`
 * (ADR-0076 §2). Exports are client-side, so this is the only way the feed sees one.
 */
export async function reportExport(period: string, options: RequestOptions) {
  const validated = ActivityExportRequestSchema.parse({ period });
  const res = await sendJson('POST', '/workstreams/activity/export', validated, options, (raw) =>
    ActivityExportResponseSchema.parse(raw),
  );
  return res.data;
}

/**
 * Accept a memory entry (ADR-0056) —
 * `POST /workstreams/{id}/memory/{entryId}/accept`. Body-less: the user keeps
 * or forgets what an agent proposed, they never author the text, so nothing a
 * human typed can reach an agent's system prompt through memory.
 */
export async function acceptMemory(
  workstreamId: string,
  entryId: string,
  options: RequestOptions,
): Promise<MemoryEntryResponse> {
  const token = await options.getAccessToken();

  const response = await fetch(
    `${config.apiBaseUrl}${API_PREFIX}/workstreams/${encodeURIComponent(
      workstreamId,
    )}/memory/${encodeURIComponent(entryId)}/accept`,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      signal: options.signal,
    },
  );

  return handleResponse(response, (raw) => MemoryEntryResponseSchema.parse(raw));
}

/**
 * Forget a memory entry (ADR-0056) —
 * `DELETE /workstreams/{id}/memory/{entryId}`. Same contract as
 * {@link deleteArtifact}: the BFF is the sole tenant gate, so a 2xx means gone.
 */
export async function deleteMemory(
  workstreamId: string,
  entryId: string,
  options: RequestOptions,
): Promise<DeleteMemoryResponse> {
  const token = await options.getAccessToken();

  const response = await fetch(
    `${config.apiBaseUrl}${API_PREFIX}/workstreams/${encodeURIComponent(
      workstreamId,
    )}/memory/${encodeURIComponent(entryId)}`,
    {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      signal: options.signal,
    },
  );

  return handleResponse(response, (raw) => DeleteMemoryResponseSchema.parse(raw));
}

/**
 * Fetch helper for the staff routes (ADR-0072/0073) — the Bearer token, JSON
 * headers and problem+json handling, for every method. A JSON body is sent only
 * when one is given.
 */
async function sendJson<T>(
  method: 'GET' | 'PUT' | 'POST' | 'DELETE',
  path: string,
  body: unknown,
  options: RequestOptions,
  parse: (raw: unknown) => T,
) {
  const token = await options.getAccessToken();
  const response = await fetch(`${config.apiBaseUrl}${API_PREFIX}${path}`, {
    method,
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal: options.signal,
  });
  return handleResponse(response, parse);
}

function getJson<T>(path: string, options: RequestOptions, parse: (raw: unknown) => T) {
  return sendJson('GET', path, undefined, options, parse);
}

/** The caller's platform permissions — `GET /me` (ADR-0072). Hides UI; the server enforces. */
export function getMe(options: RequestOptions): Promise<StaffMeResponse> {
  return getJson('/me', options, (raw) => StaffMeResponseSchema.parse(raw));
}

/** The caller's tenant role — `GET /workstreams/me` (ADR-0071 §1). UX only; the server enforces. */
export function getMyRole(options: RequestOptions): Promise<MeResponse> {
  return getJson('/workstreams/me', options, (raw) => MeResponseSchema.parse(raw));
}

/** The tenant's plan with its AUD figures — `GET /workstreams/plan` (Owner only; ADR-0071 §7). */
export function getPlan(options: RequestOptions): Promise<PlanResponse> {
  return getJson('/workstreams/plan', options, (raw) => PlanResponseSchema.parse(raw));
}

/** Budget settings + this period's status — `GET /workstreams/budget` (Owner/Admin; ADR-0078 §2). */
export function getBudget(options: RequestOptions): Promise<BudgetResponse> {
  return getJson('/workstreams/budget', options, (raw) => BudgetResponseSchema.parse(raw));
}

/** Save the budget settings — `PUT /workstreams/budget` (Owner only). Resolves with what was stored. */
export async function putBudget(
  body: BudgetSettingsRequest,
  options: RequestOptions,
): Promise<BudgetSettings> {
  const validated = BudgetSettingsRequestSchema.parse(body);
  const res = await sendJson('PUT', '/workstreams/budget', validated, options, (raw) =>
    BudgetPutResponseSchema.parse(raw),
  );
  return res.data;
}

/** The Users & Access roster + pending invites — `GET /workstreams/members` (Owner/Admin; ADR-0071 §6). */
export function listMembers(options: RequestOptions): Promise<MembersResponse> {
  return getJson('/workstreams/members', options, (raw) => MembersResponseSchema.parse(raw));
}

/** Invite by email — `POST /workstreams/members/invitations` (Owner/Admin). 409 = already a member or invited. */
export async function inviteMember(body: InviteRequest, options: RequestOptions) {
  const validated = InviteRequestSchema.parse(body);
  const res = await sendJson(
    'POST',
    '/workstreams/members/invitations',
    validated,
    options,
    (raw) => InviteResponseSchema.parse(raw),
  );
  return res.data;
}

/** Change a member's role — `PUT /workstreams/members/{sub}` (Owner only). 409 = self or last Owner. */
export async function changeMemberRole(
  userId: string,
  body: ChangeRoleRequest,
  options: RequestOptions,
) {
  const validated = ChangeRoleRequestSchema.parse(body);
  const res = await sendJson(
    'PUT',
    `/workstreams/members/${encodeURIComponent(userId)}`,
    validated,
    options,
    (raw) => MemberChangeResponseSchema.parse(raw),
  );
  return res.data;
}

/** Remove a member from the organisation — `DELETE /workstreams/members/{sub}` (Owner only). */
/** Withdraw a pending invite — `DELETE /workstreams/members/invitations/{invitationId}`. */
export async function withdrawInvite(invitationId: string, options: RequestOptions) {
  const res = await sendJson(
    'DELETE',
    `/workstreams/members/invitations/${encodeURIComponent(invitationId)}`,
    undefined,
    options,
    (raw) => InviteWithdrawnResponseSchema.parse(raw),
  );
  return res.data;
}

export async function removeMember(userId: string, options: RequestOptions) {
  const res = await sendJson(
    'DELETE',
    `/workstreams/members/${encodeURIComponent(userId)}`,
    undefined,
    options,
    (raw) => MemberRemovedResponseSchema.parse(raw),
  );
  return res.data;
}

/**
 * Raise this period's cap — `POST /workstreams/budget/extension` (Owner only; ADR-0078 §4,
 * D17-07). Resolves with the period's new extension total.
 */
export async function postBudgetExtension(
  body: BudgetExtensionRequest,
  options: RequestOptions,
): Promise<BudgetExtensionData> {
  const validated = BudgetExtensionRequestSchema.parse(body);
  const res = await sendJson('POST', '/workstreams/budget/extension', validated, options, (raw) =>
    BudgetExtensionResponseSchema.parse(raw),
  );
  return res.data;
}

/** Recent eval run headers — `GET /admin/evals/runs` (`evals:read`). */
export function listEvalRuns(options: RequestOptions) {
  return getJson('/admin/evals/runs', options, (raw) => EvalRunListResponseSchema.parse(raw));
}

/** One eval run with every case result — `GET /admin/evals/runs/{runId}` (`evals:read`). */
export function getEvalRun(runId: string, options: RequestOptions) {
  return getJson(`/admin/evals/runs/${encodeURIComponent(runId)}`, options, (raw) =>
    EvalRunResponseSchema.parse(raw),
  );
}

// ─── Eval fixtures (ADR-0073): question sets + documents ────────────────────

const setPath = (name: string) => `/admin/evals/sets/${encodeURIComponent(name)}`;
const documentPath = (name: string) => `/admin/evals/documents/${encodeURIComponent(name)}`;
const ignore = () => undefined;

/** Every question set's header — `GET /admin/evals/sets` (`evals:read`). */
export function listEvalSets(options: RequestOptions) {
  return getJson('/admin/evals/sets', options, (raw) => EvalSetListResponseSchema.parse(raw));
}

/** One set + the ETag its save must send back — `GET /admin/evals/sets/{name}`. */
export function getEvalSet(name: string, options: RequestOptions) {
  return getJson(setPath(name), options, (raw) => EvalSetResponseSchema.parse(raw));
}

/**
 * Create (`etag: null`) or save-if-unchanged — `PUT /admin/evals/sets/{name}`
 * (`evals:write`). A 409 means someone saved since this copy was read.
 */
export function putEvalSet(name: string, body: PutEvalSetRequest, options: RequestOptions) {
  const validated = PutEvalSetRequestSchema.parse(body);
  return sendJson('PUT', setPath(name), validated, options, (raw) =>
    EvalSetResponseSchema.parse(raw),
  );
}

export function deleteEvalSet(name: string, options: RequestOptions) {
  return sendJson('DELETE', setPath(name), undefined, options, ignore);
}

/** Every document in the fixtures bucket — `GET /admin/evals/documents`. */
export function listEvalDocuments(options: RequestOptions) {
  return getJson('/admin/evals/documents', options, (raw) =>
    EvalDocumentListResponseSchema.parse(raw),
  );
}

/** A text document's content + ETag (PDFs aren't readable in place). */
export function getEvalDocumentText(name: string, options: RequestOptions) {
  return getJson(documentPath(name), options, (raw) => EvalDocumentTextResponseSchema.parse(raw));
}

export function putEvalDocumentText(
  name: string,
  body: { text: string; etag: string },
  options: RequestOptions,
) {
  return sendJson('PUT', documentPath(name), body, options, (raw) =>
    EvalDocumentTextResponseSchema.parse(raw),
  );
}

export function deleteEvalDocument(name: string, options: RequestOptions) {
  return sendJson('DELETE', documentPath(name), undefined, options, ignore);
}

/** A presigned POST for a new (or replacement) document — `POST /admin/evals/documents/uploads`. */
export function beginEvalDocumentUpload(name: string, options: RequestOptions) {
  return sendJson('POST', '/admin/evals/documents/uploads', { name }, options, (raw) =>
    BeginEvalDocumentUploadResponseSchema.parse(raw),
  );
}

/** `GET` a member-agent route (ADR-0080 AB-1-2) with the caller's token. */
async function agentsRequest(
  method: 'GET' | 'PUT' | 'DELETE',
  path: string,
  options: RequestOptions,
): Promise<Response> {
  const token = await options.getAccessToken();
  return fetch(`${config.apiBaseUrl}${API_PREFIX}${path}`, {
    method,
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    signal: options.signal,
  });
}

const agentPath = (workstreamId: string, agentId?: string): string =>
  `/workstreams/${encodeURIComponent(workstreamId)}/agents` +
  (agentId === undefined ? '' : `/${encodeURIComponent(agentId)}`);

/** `GET /api/v1/workstreams/agents` — the org's shared custom agents (ADR-0080). */
export async function listOrgAgents(options: RequestOptions) {
  const response = await agentsRequest('GET', '/workstreams/agents', options);
  return handleResponse(response, (raw) => ListOrgAgentsResponseSchema.parse(raw));
}

/** `GET /api/v1/workstreams/{id}/agents` — this workstream's member agents. */
export async function listMemberAgents(workstreamId: string, options: RequestOptions) {
  const response = await agentsRequest('GET', agentPath(workstreamId), options);
  return handleResponse(response, (raw) => ListMemberAgentsResponseSchema.parse(raw));
}

/** `PUT /api/v1/workstreams/{id}/agents/{agentId}` — invite (idempotent). */
export async function inviteMemberAgent(
  workstreamId: string,
  agentId: string,
  options: RequestOptions,
) {
  const response = await agentsRequest('PUT', agentPath(workstreamId, agentId), options);
  return handleResponse(response, (raw) => MemberAgentResponseSchema.parse(raw));
}

/** `DELETE /api/v1/workstreams/{id}/agents/{agentId}` — remove (idempotent). */
export async function removeMemberAgent(
  workstreamId: string,
  agentId: string,
  options: RequestOptions,
) {
  const response = await agentsRequest('DELETE', agentPath(workstreamId, agentId), options);
  return handleResponse(response, (raw) => RemoveMemberAgentResponseSchema.parse(raw));
}

// --- AB-2-2: builder workstreams + drafts (ADR-0080) ---------------------------------------

/** `POST /api/v1/workstreams/builders` — a new builder workstream ("New agent"). */
export function createBuilder(options: RequestOptions) {
  return sendJson('POST', '/workstreams/builders', undefined, options, (raw) =>
    CreateBuilderResponseSchema.parse(raw),
  );
}

/** `GET /api/v1/workstreams/agents/drafts` — the caller's own drafts, newest first. */
export function listDrafts(options: RequestOptions) {
  return getJson('/workstreams/agents/drafts', options, (raw) =>
    ListDraftsResponseSchema.parse(raw),
  );
}

/** `GET /api/v1/workstreams/{id}/builder` — the builder's agent + every revision, oldest first. */
export function getBuilder(workstreamId: string, options: RequestOptions) {
  return getJson(`/workstreams/${encodeURIComponent(workstreamId)}/builder`, options, (raw) =>
    BuilderResponseSchema.parse(raw),
  );
}

/** `POST /api/v1/workstreams/{id}/builder/publish` — the owner publishes the latest revision (AB-2-4). */
export function publishDraft(workstreamId: string, options: RequestOptions) {
  return sendJson(
    'POST',
    `/workstreams/${encodeURIComponent(workstreamId)}/builder/publish`,
    undefined,
    options,
    (raw) => LiveVersionResponseSchema.parse(raw),
  );
}

/** `PUT /api/v1/workstreams/{id}/builder/live/{version}` — roll back to an earlier version (AB-2-4). */
export function setLiveVersion(workstreamId: string, version: number, options: RequestOptions) {
  return sendJson(
    'PUT',
    `/workstreams/${encodeURIComponent(workstreamId)}/builder/live/${version}`,
    undefined,
    options,
    (raw) => LiveVersionResponseSchema.parse(raw),
  );
}

// --- AB-2-5: the org library — share with review, retire (ADR-0081 §2/§3) ------------------

const agentAction = (agentId: string, action: string) =>
  `/workstreams/agents/${encodeURIComponent(agentId)}/${action}`;

/** `GET /api/v1/workstreams/agents/library` — the org's shared agents + the caller's own. */
export function listLibrary(options: RequestOptions) {
  return getJson('/workstreams/agents/library', options, (raw) =>
    ListLibraryResponseSchema.parse(raw),
  );
}

/** `POST /api/v1/workstreams/agents/{agentId}/share` — ask an Owner/Admin to share it. */
export function requestShare(agentId: string, options: RequestOptions) {
  return sendJson('POST', agentAction(agentId, 'share'), undefined, options, (raw) =>
    ShareRequestResponseSchema.parse(raw),
  );
}

/** `GET /api/v1/workstreams/agents/reviews` — pending share requests (Owner/Admin). */
export function listReviews(options: RequestOptions) {
  return getJson('/workstreams/agents/reviews', options, (raw) =>
    ListReviewsResponseSchema.parse(raw),
  );
}

/** `POST /api/v1/workstreams/agents/{agentId}/review/{decision}` (Owner/Admin). */
export function decideReview(
  agentId: string,
  decision: 'approve' | 'decline',
  options: RequestOptions,
) {
  return sendJson('POST', agentAction(agentId, `review/${decision}`), undefined, options, (raw) =>
    DecideReviewResponseSchema.parse(raw),
  );
}

/** `POST /api/v1/workstreams/agents/{agentId}/retire` — the owner, or an org Owner. */
export function retireAgent(agentId: string, options: RequestOptions) {
  return sendJson('POST', agentAction(agentId, 'retire'), undefined, options, (raw) =>
    RetireAgentResponseSchema.parse(raw),
  );
}
